// src/components/MultiMeshViewer.tsx

import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Typography, Box, CircularProgress, Button, Tooltip } from '@mui/material';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { convertToInternalCoords, convertFromExternalCoords, disposeObject3D, createTextSprite, convertToExternalCoords } from '../utils/threeUtils';

const meshCache = new Map<string, Promise<THREE.Object3D>>();

export interface MultiMeshViewerProps {
  meshUris: string[];
  initialZoom?: number;
  focalPoint?: [number, number, number];
  focalPoints?: Array<{ name: string; coordinates: [number, number, number]; }>;
  onMeshesLoaded?: (models: THREE.Object3D[], scene: THREE.Scene, container: HTMLDivElement, localOrigin: THREE.Vector3) => void;
  height?: string;
  onFocalPointChange?: (index: number, name: string) => void;
  onGardenSelect?: (index: number, name: string) => void;
  cameraControls?: React.MutableRefObject<OrbitControls | null>;
  sunEcefVector?: [number, number, number];
  gardenPolygons?: { [garden_uid: string]: { polygon: [number, number][]; elevation: number | null; } };
  gardenLabels?: { uid: string; name: string; ecefCoordinates: Array<[number, number, number]> }[];
  sunMarkersData?: { positionEcef: [number, number, number], inSun: boolean }[];
  showSunMarkers?: boolean;
  // New props for drawing tool integration
  onSceneReady?: (data: {
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    controls: OrbitControls;
    renderer: THREE.WebGLRenderer;
    container: HTMLDivElement;
    localOrigin: THREE.Vector3;
  }) => void;
  onCanvasClick?: (event: MouseEvent, intersection: THREE.Intersection | null, raycaster: THREE.Raycaster) => void;
  // New props for adjusting focal point to mesh surface
  adjustFocalPointToMesh?: boolean;
  onFocalPointAdjusted?: (newFocalPoint: [number, number, number]) => void;
  // New prop for disabling shadows
  disableShadows?: boolean;
}

const MultiMeshViewer: React.FC<MultiMeshViewerProps> = ({ 
  meshUris, 
  initialZoom = 55,
  focalPoint,
  focalPoints,
  onMeshesLoaded,
  height = '600px',
  onFocalPointChange,
  onGardenSelect,
  cameraControls,
  sunEcefVector = [0.916, 0.115, 0.383],
  gardenPolygons = {},
  gardenLabels = [],
  sunMarkersData = [],
  showSunMarkers = false,
  onSceneReady,
  onCanvasClick,
  adjustFocalPointToMesh = false,
  onFocalPointAdjusted,
  disableShadows = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const localRootRef = useRef<THREE.Group | null>(null);
  const localOriginRef = useRef<THREE.Vector3>(new THREE.Vector3());
  const raycasterRef = useRef(new THREE.Raycaster());
  
  const sunLightRef = useRef<THREE.DirectionalLight | null>(null);
  const sunMeshRef = useRef<THREE.Mesh | null>(null);
  const atmosphericGlowRef = useRef<THREE.Sprite | null>(null);
  const gardenOutlinesRef = useRef<THREE.Group | null>(null);
  const gardenLabelsRef = useRef<THREE.Group | null>(null);
  const sunMarkersGroupRef = useRef<THREE.Group | null>(null);

  const onMeshesLoadedRef = useRef(onMeshesLoaded);
  const onFocalPointAdjustedRef = useRef(onFocalPointAdjusted);
  const meshUrisRef = useRef<string[]>([]);
  const autoRotatingRef = useRef<boolean>(true);
  const focalPointRef = useRef(focalPoint);
  const sceneInitializedRef = useRef<boolean>(false);

  useEffect(() => {
    onFocalPointAdjustedRef.current = onFocalPointAdjusted;
  }, [onFocalPointAdjusted]);

  const colors = useMemo(() => ({
    midday: new THREE.Color(0xFFFDF8),
    sunrise: new THREE.Color(0xFF6B35),
    morning: new THREE.Color(0xFFD700),
    dawnSkyTop: new THREE.Color(0x7694CF),
    dawnSkyBottom: new THREE.Color(0xFFB580),
    daySkyTop: new THREE.Color(0x6DB3F2),
    daySkyBottom: new THREE.Color(0xB8D4E3),
    nightSkyTop: new THREE.Color(0x0C1445),
    nightSkyBottom: new THREE.Color(0x1E3A5F),
    sunMarker: new THREE.Color('#FFD700'),
    shadeMarker: new THREE.Color('#404055'),
  }), []);

  const markerTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 64; 
    canvas.height = 64;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.beginPath();
    context.arc(32, 32, 28, 0, 2 * Math.PI);
    context.fillStyle = 'white';
    context.fill();
    return new THREE.CanvasTexture(canvas);
  }, []);

  useEffect(() => {
    onMeshesLoadedRef.current = onMeshesLoaded;
  }, [onMeshesLoaded]);
  
  useEffect(() => {
    focalPointRef.current = focalPoint;
  }, [focalPoint]);
  
  useEffect(() => {
    if (cameraControls && controlsRef.current) {
      cameraControls.current = controlsRef.current;
    }
  }, [cameraControls]);

  const updateSunAndAtmosphere = useCallback((ecefVector: [number, number, number] | undefined, focalPointEcef: [number, number, number]) => {
    const scene = sceneRef.current;
    const sunLight = sunLightRef.current;
    const sunMesh = sunMeshRef.current;
    const glow = atmosphericGlowRef.current;
    const renderer = rendererRef.current;
    if (!scene || !sunLight || !sunMesh || !glow || !renderer) {
      return;
    }

    // If shadows are disabled, maintain consistent bright lighting
    if (disableShadows) {
      sunLight.visible = true;
      sunMesh.visible = false;
      glow.visible = false;
      scene.background = new THREE.Color(0xf0f0f0);
      scene.fog = null;
      return;
    }

    if (!ecefVector) {
        sunLight.visible = false;
        sunMesh.visible = false;
        glow.visible = false;
        scene.background = colors.nightSkyTop;
        scene.fog = new THREE.Fog(colors.nightSkyBottom, 100, 500);
        return;
    }

    const [ecefX, ecefY, ecefZ] = ecefVector;
    const localUpVector = new THREE.Vector3(...focalPointEcef).normalize();
    const sunHeight = Math.max(0, ecefX * localUpVector.x + ecefY * localUpVector.y + ecefZ * localUpVector.z);

    if (sunHeight <= 0) {
        sunLight.visible = false;
        sunMesh.visible = false;
        glow.visible = false;
        scene.background = colors.nightSkyTop;
        scene.fog = new THREE.Fog(colors.nightSkyBottom, 100, 500);
        return;
    }

    sunLight.visible = true;
    sunMesh.visible = true;

    const sunVectorThree = new THREE.Vector3(ecefX, ecefZ, -ecefY);
    const sunVisualDistance = 200;
    const sunPosition = sunVectorThree.clone().multiplyScalar(sunVisualDistance);
    
    sunLight.position.copy(sunPosition);
    sunLight.target.position.set(0, 0, 0);
    sunMesh.position.copy(sunPosition);
    glow.position.copy(sunPosition);
    
    const sunElevation = Math.asin(Math.min(1, Math.max(-1, sunHeight)));
    const elevationDegrees = sunElevation * (180 / Math.PI);
    
    let currentSunColor = new THREE.Color();
    let skyTopColor = new THREE.Color();
    let skyBottomColor = new THREE.Color();
    let glowIntensity = 0;
    
    if (elevationDegrees < 5) {
        const t = elevationDegrees / 5;
        currentSunColor.copy(colors.sunrise);
        skyTopColor.lerpColors(colors.dawnSkyTop, new THREE.Color(0xF4A460), t * 0.5);
        skyBottomColor.lerpColors(colors.dawnSkyBottom, new THREE.Color(0xFF6B35), t);
        glowIntensity = 1.0;
        sunLight.intensity = 2.0 + t * 0.5;
    } else if (elevationDegrees < 15) {
        const t = (elevationDegrees - 5) / 10;
        currentSunColor.lerpColors(colors.sunrise, colors.morning, t);
        skyTopColor.lerpColors(new THREE.Color(0xF4A460), colors.dawnSkyTop, t);
        skyBottomColor.lerpColors(new THREE.Color(0xFF6B35), colors.daySkyBottom, t * 0.7);
        glowIntensity = 1.0 - t * 0.5;
        sunLight.intensity = 2.5 + t * 0.5;
    } else if (elevationDegrees < 30) {
        const t = (elevationDegrees - 15) / 15;
        currentSunColor.lerpColors(colors.morning, colors.midday, t);
        skyTopColor.lerpColors(colors.dawnSkyTop, colors.daySkyTop, t);
        skyBottomColor.lerpColors(colors.daySkyBottom, colors.daySkyBottom, t);
        glowIntensity = 0.5 - t * 0.3;
        sunLight.intensity = 3.0;
    } else {
        currentSunColor.copy(colors.midday);
        skyTopColor.copy(colors.daySkyTop);
        skyBottomColor.copy(colors.daySkyBottom);
        glowIntensity = 0.1;
        sunLight.intensity = 3.0;
    }
    
    sunLight.color.copy(currentSunColor);
    (sunMesh.material as THREE.MeshBasicMaterial).color.copy(currentSunColor);
    
    glow.visible = glowIntensity > 0;
    (glow.material as THREE.SpriteMaterial).opacity = glowIntensity * 0.3;
    (glow.material as THREE.SpriteMaterial).color.copy(currentSunColor);

    const skyMixFactor = Math.min(1, sunHeight * 2);
    const mixedSkyColor = new THREE.Color().lerpColors(skyBottomColor, skyTopColor, skyMixFactor);
    scene.background = mixedSkyColor;
    scene.fog = new THREE.Fog(skyBottomColor, 100, 500);

    renderer.toneMappingExposure = elevationDegrees < 15 ? 1.1 : 0.95;
    sunLight.shadow.needsUpdate = true;
  }, [colors, disableShadows]);

  const drawGardenOutlines = useCallback(() => {
    if (!sceneRef.current) return;
    if (gardenOutlinesRef.current) {
      disposeObject3D(gardenOutlinesRef.current);
    }

    const newOutlines = new THREE.Group();
    newOutlines.name = 'GardenOutlines';

    Object.values(gardenPolygons).forEach(garden => {
      if (!garden.polygon || garden.polygon.length < 2) return;

      const worldPoints = garden.polygon.map(p => 
        convertToInternalCoords(convertFromExternalCoords([p[0], p[1], garden.elevation ?? 0]).toArray())
      );
      const localPoints = worldPoints.map(p => p.clone().sub(localOriginRef.current));

      const path = new THREE.CurvePath<THREE.Vector3>();
      const closedLoop = [...localPoints, localPoints[0]];
      for (let i = 0; i < closedLoop.length - 1; i++) {
        path.add(new THREE.LineCurve3(closedLoop[i], closedLoop[i + 1]));
      }

      const glowTube = new THREE.Mesh(
        new THREE.TubeGeometry(path, 64, 0.05, 8, true),
        new THREE.MeshBasicMaterial({ color: 0xF6B12D, transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false })
      );
      const coreTube = new THREE.Mesh(
        new THREE.TubeGeometry(path, 64, 0.01, 8, true),
        new THREE.MeshBasicMaterial({ color: 0xEB4F2F, transparent: true, opacity: 0.9, depthTest: false, depthWrite: false })
      );
      
      newOutlines.add(glowTube, coreTube);
    });

    gardenOutlinesRef.current = newOutlines;
    sceneRef.current.add(newOutlines);
  }, [gardenPolygons]);

  const calculateCentroid = (coords: Array<[number, number, number]>) => {
    if (coords.length === 0) return [0, 0, 0] as [number, number, number];
    const sum = coords.reduce((acc, c) => [acc[0] + c[0], acc[1] + c[1], acc[2] + c[2]], [0, 0, 0]);
    return [sum[0] / coords.length, sum[1] / coords.length, sum[2] / coords.length] as [number, number, number];
  };

  const drawGardenLabels = useCallback(() => {
    if (!sceneRef.current) return;
    if (gardenLabelsRef.current) {
        disposeObject3D(gardenLabelsRef.current);
    }
    const newLabels = new THREE.Group();
    newLabels.name = 'GardenLabels';

    gardenLabels.forEach(garden => {
        if (!garden.ecefCoordinates || garden.ecefCoordinates.length === 0) return;
        const centroidEcef = calculateCentroid(garden.ecefCoordinates);
        const labelWorldPos = convertToInternalCoords(centroidEcef);
        const labelLocalPos = labelWorldPos.clone().sub(localOriginRef.current);
        const upVector = new THREE.Vector3(...centroidEcef).normalize();
        const threeUp = new THREE.Vector3(upVector.x, upVector.z, -upVector.y);

        const textSprite = createTextSprite(garden.name);
        textSprite.position.copy(labelLocalPos).add(threeUp.multiplyScalar(6));
        newLabels.add(textSprite);
    });

    gardenLabelsRef.current = newLabels;
    sceneRef.current.add(newLabels);
  }, [gardenLabels]);

  useEffect(() => {
    if (focalPoint) {
      updateSunAndAtmosphere(sunEcefVector, focalPoint);
    }
  }, [sunEcefVector, focalPoint, updateSunAndAtmosphere]);

  // Handle focal point changes to move camera
  useEffect(() => {
    if (!focalPoint || !controlsRef.current || !cameraRef.current || !localRootRef.current) return;
    
    if (!sceneRef.current || !rendererRef.current || !sceneInitializedRef.current) return;
    
    if (!localOriginRef.current || localOriginRef.current.lengthSq() === 0) return;
    
    const newLocalOrigin = convertToInternalCoords(focalPoint);
    const oldLocalOrigin = localOriginRef.current;
    
    if (newLocalOrigin.distanceTo(oldLocalOrigin) < 0.001) return;
    
    const shiftVector = new THREE.Vector3().subVectors(newLocalOrigin, oldLocalOrigin);
    
    localRootRef.current.position.sub(shiftVector);
    
    localOriginRef.current.copy(newLocalOrigin);
    
    const newUpVector = newLocalOrigin.clone().normalize();
    cameraRef.current.up.copy(newUpVector);
    
    const cameraDistance = initialZoom || 55;
    const tangent = new THREE.Vector3(1, 0, 0);
    if (Math.abs(newUpVector.dot(tangent)) > 0.9) {
      tangent.set(0, 0, 1);
    }
    
    const sideVector = new THREE.Vector3().crossVectors(newUpVector, tangent).normalize();
    const cameraDirection = new THREE.Vector3()
      .addScaledVector(newUpVector, Math.cos(Math.PI/4))
      .addScaledVector(sideVector, Math.sin(Math.PI/4))
      .normalize();
    
    cameraRef.current.position.copy(cameraDirection).multiplyScalar(cameraDistance);
    
    controlsRef.current.target.set(0, 0, 0);
    
    controlsRef.current.update();
    
    autoRotatingRef.current = true;
    
    drawGardenOutlines();
    drawGardenLabels();
  }, [focalPoint, initialZoom, drawGardenOutlines, drawGardenLabels]);

  useEffect(() => {
    drawGardenOutlines();
  }, [gardenPolygons, localOriginRef.current]);
  
  useEffect(() => {
    drawGardenLabels();
  }, [gardenLabels, localOriginRef.current]);
  
  useEffect(() => {
    if (sunMarkersGroupRef.current) {
        sunMarkersGroupRef.current.visible = showSunMarkers;
    }
  }, [showSunMarkers]);

  useEffect(() => {
    if (!markerTexture) return;

    if (!sunMarkersGroupRef.current || sunMarkersGroupRef.current.children.length !== sunMarkersData.length) {
        if (sunMarkersGroupRef.current) {
            disposeObject3D(sunMarkersGroupRef.current);
        }
        const newMarkers = new THREE.Group();
        newMarkers.name = 'SunMarkers';
        sunMarkersData.forEach(() => {
            const material = new THREE.SpriteMaterial({ map: markerTexture, depthTest: false, transparent: true, opacity: 0.85 });
            const sprite = new THREE.Sprite(material);
            sprite.scale.set(0.4, 0.4, 1);
            newMarkers.add(sprite);
        });
        sunMarkersGroupRef.current = newMarkers;
        sceneRef.current?.add(newMarkers);
    }

    sunMarkersGroupRef.current.children.forEach((sprite, index) => {
        const markerData = sunMarkersData[index];
        if (markerData && sprite instanceof THREE.Sprite) {
            const worldPos = convertToInternalCoords(markerData.positionEcef);
            sprite.position.copy(worldPos.clone().sub(localOriginRef.current)).add(new THREE.Vector3(0, 0.1, 0));
            (sprite.material as THREE.SpriteMaterial).color.copy(markerData.inSun ? colors.sunMarker : colors.shadeMarker);
        }
    });

    sunMarkersGroupRef.current.visible = showSunMarkers;

  }, [sunMarkersData, markerTexture, colors, showSunMarkers, focalPoint]);

  const handleViewerFocalPointChange = (coordinates: [number, number, number], index: number, name: string) => {
    if (!controlsRef.current || !cameraRef.current) return;
    
    const newLocalOrigin = convertToInternalCoords(coordinates);
    const oldLocalOrigin = localOriginRef.current;
    const shiftVector = new THREE.Vector3().subVectors(newLocalOrigin, oldLocalOrigin);

    cameraRef.current.position.sub(shiftVector);

    if (localRootRef.current) {
        localRootRef.current.position.sub(shiftVector);
    }
    
    localOriginRef.current.copy(newLocalOrigin);
    const newUpVector = newLocalOrigin.clone().normalize();
    cameraRef.current.up.copy(newUpVector);
    
    controlsRef.current.update();
    autoRotatingRef.current = true;
    
    if (onFocalPointChange) {
        onFocalPointChange(index, name);
    }
    if (onGardenSelect) {
        onGardenSelect(index, name);
    }
  };

  const handleCanvasClick = useCallback((event: MouseEvent) => {
    if (!onCanvasClick || !containerRef.current || !cameraRef.current || !sceneRef.current || !localRootRef.current) {
        return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const raycaster = raycasterRef.current;
    raycaster.setFromCamera(new THREE.Vector2(x, y), cameraRef.current);

    // Intersect with the main loaded models, which are in localRootRef
    const intersects = raycaster.intersectObject(localRootRef.current, true);

    onCanvasClick(event, intersects.length > 0 ? intersects[0] : null, raycaster);
  }, [onCanvasClick]);
  
  const loadMeshWithCache = async (uri: string, gltfLoader: GLTFLoader): Promise<THREE.Object3D> => {
    if (meshCache.has(uri)) {
      return meshCache.get(uri)!.then(model => model.clone());
    }
    const meshPromise = gltfLoader.loadAsync(uri).then(gltf => {
        gltf.scene.traverse(child => {
            if (child instanceof THREE.Mesh) {
              child.material = new THREE.MeshStandardMaterial({
                  color: 0xffffff,
                  roughness: 0.85,
                  metalness: 0.1,
                  vertexColors: !!child.geometry.attributes.color,
                  map: (child.material as THREE.MeshStandardMaterial).map || null,
              });
              child.geometry.computeVertexNormals();
              child.castShadow = !disableShadows;
              child.receiveShadow = !disableShadows;
            }
        });
        return gltf.scene;
    });
    meshCache.set(uri, meshPromise);
    return meshPromise.then(model => model.clone());
  };

  useEffect(() => {
    if (!containerRef.current || meshUris.length === 0) return;
    
    const currentContainer = containerRef.current;
    if (meshUrisRef.current.length === meshUris.length && meshUrisRef.current.every((uri, i) => uri === meshUris[i])) {
      setLoading(false);
      return;
    }
    
    meshUrisRef.current = [...meshUris];
    setLoading(true);
    setProgress(0);
    setError(null);
    
    if (!sceneRef.current) {
      const scene = new THREE.Scene();
      
      if (disableShadows) {
        // Bright, uniform lighting without shadows
        scene.add(new THREE.HemisphereLight(0xffffff, 0xcccccc, 1.2));
        scene.add(new THREE.AmbientLight(0xffffff, 0.8));
        
        const light = new THREE.DirectionalLight(0xffffff, 0.6);
        light.castShadow = false;
        scene.add(light);
        scene.add(light.target);
        sunLightRef.current = light;
      } else {
        // Original lighting with shadows
        scene.add(new THREE.HemisphereLight(0xffffbb, 0x080820, 0.25));
        scene.add(new THREE.AmbientLight(0xffffff, 0.1));
        
        const light = new THREE.DirectionalLight(0xffffff, 1.0);
        light.castShadow = true;
        light.shadow.mapSize.set(4096, 4096);
        const shadowCameraSize = 150;
        light.shadow.camera.left = -shadowCameraSize;
        light.shadow.camera.right = shadowCameraSize;
        light.shadow.camera.top = shadowCameraSize;
        light.shadow.camera.bottom = -shadowCameraSize;
        light.shadow.camera.near = 1;
        light.shadow.camera.far = 500;
        light.shadow.bias = -0.001;
        scene.add(light);
        scene.add(light.target);
        sunLightRef.current = light;
      }

      const sunMesh = new THREE.Mesh(new THREE.SphereGeometry(3, 32, 32), new THREE.MeshBasicMaterial({ color: 0xffff00 }));
      scene.add(sunMesh);
      sunMeshRef.current = sunMesh;

      const glowCanvas = document.createElement('canvas');
      glowCanvas.width = 256;
      glowCanvas.height = 256;
      const ctx = glowCanvas.getContext('2d');
      if (ctx) {
          const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
          gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
          gradient.addColorStop(1, 'rgba(255, 200, 150, 0)');
          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, 256, 256);
      }
      const glowMaterial = new THREE.SpriteMaterial({
          map: new THREE.CanvasTexture(glowCanvas),
          color: 0xFFD700,
          blending: THREE.AdditiveBlending,
          opacity: 0.3,
          depthWrite: false,
      });
      const glowSprite = new THREE.Sprite(glowMaterial);
      glowSprite.scale.set(40, 40, 1);
      scene.add(glowSprite);
      atmosphericGlowRef.current = glowSprite;

      sceneRef.current = scene;

      const localRoot = new THREE.Group();
      localRoot.name = 'LocalRoot';
      scene.add(localRoot);
      localRootRef.current = localRoot;
    } else {
      if (localRootRef.current) {
        while (localRootRef.current.children.length > 0) {
            localRootRef.current.remove(localRootRef.current.children[0]);
        }
      }
    }
    
    if (!cameraRef.current) {
      cameraRef.current = new THREE.PerspectiveCamera(60, currentContainer.clientWidth / currentContainer.clientHeight, 0.1, 10000);
    }
    
    if (!rendererRef.current) {
      const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
      renderer.setSize(currentContainer.clientWidth, currentContainer.clientHeight);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.shadowMap.enabled = !disableShadows;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      currentContainer.appendChild(renderer.domElement);
      rendererRef.current = renderer;
    }
    
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
    const gltfLoader = new GLTFLoader();
    gltfLoader.setDRACOLoader(dracoLoader);
    
    let loadedCount = 0;
    const loadPromises = meshUris.map(uri => 
      loadMeshWithCache(uri, gltfLoader)
        .then(model => {
          loadedCount++;
          setProgress((loadedCount / meshUris.length) * 100);
          localRootRef.current?.add(model);
          return model;
        })
        .catch(err => {
          loadedCount++;
          setProgress((loadedCount / meshUris.length) * 100);
          setError(`Failed to load mesh: ${uri}`);
          return null;
        })
    );
    
    const setupSceneAfterLoading = (models: THREE.Object3D[], container: HTMLDivElement) => {
      if (!sceneRef.current || !cameraRef.current || !rendererRef.current || !localRootRef.current) return;
      
      const scene = sceneRef.current;
      const camera = cameraRef.current;
      const renderer = rendererRef.current;
      
      const combinedBox = new THREE.Box3();
      models.forEach(model => {
        combinedBox.union(new THREE.Box3().setFromObject(model));
      });
      
      let localOrigin = focalPointRef.current ? convertToInternalCoords(focalPointRef.current) : combinedBox.getCenter(new THREE.Vector3());
      
      if (adjustFocalPointToMesh && focalPointRef.current && localRootRef.current) {
        const initialFocalPointWorld = convertToInternalCoords(focalPointRef.current);
        const worldUpVector = initialFocalPointWorld.clone().normalize();
  
        const raycaster = new THREE.Raycaster();
        let closestIntersection: THREE.Intersection | null = null;
        let minDistance = Infinity;
        
        const directions = [worldUpVector.clone().negate(), worldUpVector];
        const startPoints = [
            initialFocalPointWorld.clone().add(worldUpVector.clone().multiplyScalar(500)), // From above
            initialFocalPointWorld.clone().add(worldUpVector.clone().multiplyScalar(-500)) // From below
        ];
        
        for (let i = 0; i < 2; i++) {
            raycaster.set(startPoints[i], directions[i]);
            const intersects = raycaster.intersectObject(localRootRef.current, true);
            
            if (intersects.length > 0) {
                const intersectPoint = intersects[0].point;
                const distance = intersectPoint.distanceTo(initialFocalPointWorld);
                if (distance < minDistance) {
                    minDistance = distance;
                    closestIntersection = intersects[0];
                }
            }
        }
        
        if (closestIntersection) {
            localOrigin.copy(closestIntersection.point);
            if (onFocalPointAdjustedRef.current) {
                const newFocalPointEcef = convertToExternalCoords(localOrigin);
                onFocalPointAdjustedRef.current(newFocalPointEcef);
            }
        } else {
            console.warn("Could not find intersection to adjust focal point. Using original.");
        }
      }
      
      localOriginRef.current.copy(localOrigin);
      localRootRef.current.position.copy(localOrigin).negate();
      
      const upVector = localOrigin.clone().normalize();
      camera.up.copy(upVector);
      
      const fov = camera.fov * (Math.PI / 180);
      const cameraDistance = initialZoom || Math.abs(combinedBox.getSize(new THREE.Vector3()).length() / Math.sin(fov / 2));
      
      const tangent = new THREE.Vector3(1, 0, 0);
      if (Math.abs(upVector.dot(tangent)) > 0.9) {
        tangent.set(0, 0, 1);
      }
      
      const sideVector = new THREE.Vector3().crossVectors(upVector, tangent).normalize();
      const cameraDirection = new THREE.Vector3()
        .addScaledVector(upVector, Math.cos(Math.PI/4))
        .addScaledVector(sideVector, Math.sin(Math.PI/4))
        .normalize();
      
      camera.position.copy(cameraDirection).multiplyScalar(cameraDistance);
      camera.lookAt(new THREE.Vector3(0, 0, 0));

      if (controlsRef.current) {
        controlsRef.current.target.set(0, 0, 0);
      } else {
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.maxPolarAngle = Math.PI / 2;
        controls.target.set(0, 0, 0);
        
        const handleUserInteraction = () => { autoRotatingRef.current = false; };
        renderer.domElement.addEventListener('mousedown', handleUserInteraction);
        renderer.domElement.addEventListener('wheel', handleUserInteraction);
        renderer.domElement.addEventListener('touchstart', handleUserInteraction);
        
        controlsRef.current = controls;
      }
      
      controlsRef.current.update();
      
      if (animationFrameRef.current === null) {
        animate();
      }
      
      drawGardenOutlines();
      drawGardenLabels();
      
      if (onMeshesLoadedRef.current) {
        onMeshesLoadedRef.current(models, scene, container, localOriginRef.current);
      }
      
      autoRotatingRef.current = true;
      if (focalPointRef.current) {
        updateSunAndAtmosphere(sunEcefVector, focalPointRef.current);
      }

      if (onSceneReady && sceneRef.current && cameraRef.current && rendererRef.current && controlsRef.current && containerRef.current && localOriginRef.current) {
        onSceneReady({
            scene: sceneRef.current,
            camera: cameraRef.current,
            controls: controlsRef.current,
            renderer: rendererRef.current,
            container: containerRef.current,
            localOrigin: localOriginRef.current,
        });
      }
      
      sceneInitializedRef.current = true;
    };
    
    Promise.all(loadPromises).then(models => {
        const validModels = models.filter((m): m is THREE.Object3D => m !== null);
        if (validModels.length === 0) {
            setLoading(false);
            return;
        }
        setupSceneAfterLoading(validModels, currentContainer);
        setLoading(false);
    });
    
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      if (!sceneRef.current || !cameraRef.current || !rendererRef.current || !controlsRef.current) {
        return;
      }
      controlsRef.current.autoRotate = autoRotatingRef.current;
      controlsRef.current.autoRotateSpeed = 0.5;
      controlsRef.current.update();
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    };
    
    const handleResize = () => {
        if (cameraRef.current && rendererRef.current && containerRef.current) {
            cameraRef.current.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
            cameraRef.current.updateProjectionMatrix();
            rendererRef.current.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
        }
    };
    window.addEventListener('resize', handleResize);

    if (onCanvasClick && currentContainer) {
      currentContainer.addEventListener('click', handleCanvasClick);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      sceneInitializedRef.current = false;

      if (onCanvasClick && currentContainer) {
        currentContainer.removeEventListener('click', handleCanvasClick);
      }
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      if (controlsRef.current) {
        controlsRef.current.dispose();
        controlsRef.current = null;
      }

      if (sceneRef.current) {
        disposeObject3D(sceneRef.current);
        sceneRef.current = null;
      }

      if (rendererRef.current) {
        rendererRef.current.dispose();
        rendererRef.current.forceContextLoss();
        if (containerRef.current && rendererRef.current.domElement.parentNode === containerRef.current) {
          containerRef.current.removeChild(rendererRef.current.domElement);
        }
        rendererRef.current = null;
      }
    };
  }, [meshUris, initialZoom, onCanvasClick, adjustFocalPointToMesh, disableShadows]);
  
  return (
    <Box sx={{ position: 'relative', width: '100%', height }}>
      {loading && (
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'white', textShadow: '0 1px 3px rgba(0,0,0,0.5)', zIndex: 1 }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2 }}>Loading 3D Tiles ({Math.round(progress)}%)</Typography>
        </Box>
      )}
      {error && (
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'white', textShadow: '0 1px 3px rgba(0,0,0,0.5)', textAlign: 'center', zIndex: 1 }}>
          <Typography color="error">Error</Typography>
          <Typography>{error}</Typography>
        </Box>
      )}
      {focalPoints && focalPoints.length > 0 && (
        <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {focalPoints.map((point, index) => (
            <Tooltip key={index} title={point.name} placement="bottom">
              <Button 
                variant="contained" 
                size="small"
                onClick={() => handleViewerFocalPointChange(point.coordinates, index, point.name)}
                sx={{ 
                  minWidth: 32, height: 32, borderRadius: '50%', 
                  bgcolor: 'rgba(255,255,255,0.8)', color: 'black',
                  '&:hover': { bgcolor: 'white' } 
                }}
              >
                {index + 1}
              </Button>
            </Tooltip>
          ))}
        </Box>
      )}
      <div 
        ref={containerRef} 
        style={{ 
          width: '100%', 
          height: '100%',
          position: 'relative',
        }}
      />
    </Box>
  );
}

export default MultiMeshViewer;